import React, { useEffect, useState } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';

interface PackSummary {
  id: string;
  name: string;
  memberCount: number;
}

export function ExportPage() {
  const [packs, setPacks] = useState<PackSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    loadPacks();
  }, []);

  async function loadPacks() {
    setLoading(true);
    try {
      const res = await apiFetch<PackSummary[]>('/api/admin/packs');
      if (res.success && res.data) {
        setPacks(res.data);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }

  async function exportFull() {
    setExporting(true);
    setStatus('Exporting full database...');
    try {
      const res = await apiFetch<unknown>('/api/admin/export/full');
      if (res.success && res.data) {
        downloadJson(res.data, `grimsthorpe-full-export-${dateStamp()}.json`);
        setStatus('Full export downloaded successfully.');
      } else {
        setStatus(`Export failed: ${res.error}`);
      }
    } catch {
      setStatus('Export failed: network error');
    } finally {
      setExporting(false);
    }
  }

  async function exportPack(packId: string, packName: string) {
    setExporting(true);
    setStatus(`Exporting pack "${packName}"...`);
    try {
      const res = await apiFetch<unknown>(`/api/admin/export/pack/${packId}`);
      if (res.success && res.data) {
        const safeName = packName.toLowerCase().replace(/[^a-z0-9]/g, '-');
        downloadJson(res.data, `grimsthorpe-pack-${safeName}-${dateStamp()}.json`);
        setStatus(`Pack "${packName}" exported successfully.`);
      } else {
        setStatus(`Export failed: ${res.error}`);
      }
    } catch {
      setStatus('Export failed: network error');
    } finally {
      setExporting(false);
    }
  }

  function downloadJson(data: unknown, filename: string) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function dateStamp(): string {
    return new Date().toISOString().split('T')[0];
  }

  return (
    <div>
      <h1 style={styles.pageTitle}>Export</h1>
      <p style={styles.pageSubtitle}>Download database exports as JSON files</p>

      {status && (
        <div style={styles.statusBar}>
          {status}
        </div>
      )}

      {/* Full Export */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Full Database Export</h2>
        <p style={styles.cardDesc}>
          Export the entire Grimsthorpe Lake database including all users, packs, stays,
          cabins, equipment, map data, almanac configuration, and audit logs.
        </p>
        <button
          style={styles.btnPrimary}
          onClick={exportFull}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : 'Export Full Database'}
        </button>
      </div>

      {/* Per-Pack Export */}
      <div style={{ ...styles.card, marginTop: 16 }}>
        <h2 style={styles.cardTitle}>Per-Pack Export</h2>
        <p style={styles.cardDesc}>
          Export a single pack's data including members, stays, messages, and notes.
        </p>

        {loading ? (
          <p style={styles.loading}>Loading packs...</p>
        ) : packs.length === 0 ? (
          <p style={styles.empty}>No packs available</p>
        ) : (
          <div style={styles.packGrid}>
            {packs.map((pack) => (
              <div key={pack.id} style={styles.packItem}>
                <div>
                  <div style={styles.packName}>{pack.name}</div>
                  <div style={styles.packMeta}>{pack.memberCount} members</div>
                </div>
                <button
                  style={styles.btnSmall}
                  onClick={() => exportPack(pack.id, pack.name)}
                  disabled={exporting}
                >
                  Export
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pageTitle: { fontSize: 28, fontWeight: 700, color: COLORS.textPrimary, margin: 0 },
  pageSubtitle: { fontSize: 14, color: COLORS.textMuted, marginTop: 4, marginBottom: 24 },
  card: {
    backgroundColor: COLORS.backgroundSecondary, border: `1px solid ${COLORS.borderDefault}`,
    borderRadius: 12, padding: 24,
  },
  cardTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 8 },
  cardDesc: { fontSize: 13, color: COLORS.textMuted, marginBottom: 16, lineHeight: 1.5 },
  statusBar: {
    padding: '10px 16px', backgroundColor: 'rgba(45, 90, 45, 0.15)',
    border: `1px solid ${COLORS.primaryGreenLight}`, borderRadius: 8,
    color: COLORS.brassLight, fontSize: 13, marginBottom: 20,
  },
  packGrid: { display: 'flex', flexDirection: 'column', gap: 8 },
  packItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', backgroundColor: COLORS.backgroundTertiary,
    borderRadius: 8, border: `1px solid ${COLORS.borderDefault}`,
  },
  packName: { fontSize: 14, fontWeight: 500, color: COLORS.textPrimary },
  packMeta: { fontSize: 12, color: COLORS.textMuted, marginTop: 2 },
  empty: { color: COLORS.textMuted, fontSize: 13, fontStyle: 'italic' },
  loading: { color: COLORS.textMuted, fontSize: 13 },
  btnPrimary: {
    padding: '10px 20px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold,
    border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  btnSmall: {
    padding: '6px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.brassLight,
    border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 500, cursor: 'pointer',
  },
};
